import { useEffect, useMemo, useRef, useState } from 'react';

import { AccountStat, Money as MoneyModel } from 'api-client';
import { createStatsProvider } from 'di';
import { useErrorCallback } from 'hooks';

import { formatRoiIrr } from './packageUtils';

type MainStatItem =
  | { label: string; money: MoneyModel; tooltip?: string }
  | { label: string; value: string; tooltip?: string };

const EMPTY_STATS: MainStatItem[] = [
  { label: 'ИНВЕСТИРОВАНО', value: '—' },
  { label: 'ВОЗВРАЩЕНО', value: '—' },
  { label: 'ПРОГНОЗ ВОЗВРАТА', value: '—' },
  {
    label: 'ФАКТ ROI / IRR',
    value: '— / —',
    tooltip: 'Фактическая доходность по основному счету',
  },
];

const mapAccountStatToItems = (stat: AccountStat): MainStatItem[] => [
  { label: 'ИНВЕСТИРОВАНО', money: stat.invested },
  { label: 'ВОЗВРАЩЕНО', money: stat.returned },
  { label: 'ПРОГНОЗ ВОЗВРАТА', money: stat.recoveryForecast },
  {
    label: 'ФАКТ ROI / IRR',
    value: formatRoiIrr(stat.factRoi, stat.factIrr),
    tooltip: 'Фактическая доходность по основному счету',
  },
];

export const useMainAccountStats = (accountUuid: string | undefined) => {
  const handleError = useErrorCallback();
  const handleErrorRef = useRef(handleError);
  const [accountStat, setAccountStat] = useState<AccountStat | null>(null);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (!accountUuid) {
      setAccountStat(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const statsProvider = await createStatsProvider();
        const stat = await statsProvider.getAccountStat(accountUuid);

        if (!cancelled) {
          setAccountStat(stat);
        }
      } catch (error) {
        if (!cancelled && error instanceof Error) {
          await handleErrorRef.current(error);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [accountUuid]);

  return useMemo(
    () => (accountStat ? mapAccountStatToItems(accountStat) : EMPTY_STATS),
    [accountStat]
  );
};
